import { type CrmObject } from '@playbooks/core/src/interfaces/ConnectToCrmWidget'
import { ElementWithCodeAndValue } from '@playbooks/core/src/interfaces/ActionCard'
import replaceWidgetStaticFields from '../utils/replaceWidgetStaticFields'

export interface HttpServiceConfig {
  baseUrl: string | undefined | null
  token: string | undefined | null
}

export type KeyValueData = { [key: string]: any }

const AUTH_ERROR_MESSAGE = 'NO_OAUTH_TOKEN'
class CustomError extends Error {
  public status: number | undefined

  constructor(message: string, status: number | undefined = undefined) {
    super(message)
    this.status = status
  }
}

/**
 * Service for making HTTP requests to the backend.
 * @story User Story 8109: Productize AI-supported sections
 */
export default class HttpService {
  private baseURL: string | undefined | null
  private token: string | undefined | null

  constructor(config: HttpServiceConfig) {
    this.baseURL = config.baseUrl
    this.token = config.token
  }

  /**
   * Get the generic headers needed for making requests to the backend.
   */
  private getRequestHeaders(): HeadersInit {
    const headers: HeadersInit = {}
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`
    }
    return headers
  }

  public async createCrmObjects(
    crmSystem: string,
    crmObjects: CrmObject[],
    projectId: number | string,
    data: KeyValueData,
    staticFields: KeyValueData,
    elementsWithCode: ElementWithCodeAndValue[],
    onUnauthorized?: (text: string) => void,
  ) {
    staticFields = replaceWidgetStaticFields(staticFields, elementsWithCode)

    if (!crmObjects.length) {
      return []
    }

    const objectTypes = crmObjects.map(obj => obj.objectType)

    const response = await fetch(
      `${this.baseURL}/create-crm-objects?crmSystem=${crmSystem}&crmObjects=${objectTypes.toString()}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...this.getRequestHeaders(),
        },
        body: JSON.stringify({
          room: projectId,
          data: data,
          staticFields: staticFields,
        }),
      },
    )

    if (!response.ok) {
      const res = await handleUnAuthorized(response, onUnauthorized)
      if (res) return []
      console.error('Failed to create CRM object:', response.statusText)
      throw new CustomError('Server error status ' + response.status, response.status)
    }

    const res = (await response.json()) as CrmObject[]

    return res
  }

  public async syncCrmObjects(
    crmSystem: string,
    projectId: number | string,
    data: CrmObject[],
    onUnauthorized?: (text: string) => void,
  ) {
    if (!data.length) {
      return []
    }

    const response = await fetch(`${this.baseURL}/sync-crm-objects?crmSystem=${crmSystem}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...this.getRequestHeaders(),
      },
      body: JSON.stringify({
        room: projectId,
        foundCrmObjects: data,
      }),
    })

    if (!response.ok) {
      const res = await handleUnAuthorized(response, onUnauthorized)
      if (res) return null
    }
  }

  public async findCrmObjects(
    crmSystem: string,
    crmObject: string,
    projectId: number | string,
    data: any,
    staticFields: KeyValueData,
    onUnauthorized?: (text: string) => void,
  ) {
    // return []
    const response = await fetch(`${this.baseURL}/find-crm-objects?crmSystem=${crmSystem}&crmObject=${crmObject}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...this.getRequestHeaders(),
      },
      body: JSON.stringify({
        room: projectId,
        data: data,
        staticFields: staticFields,
      }),
    })

    if (!response.ok) {
      if (response.status === 404) {
        return []
      }
      const res = await handleUnAuthorized(response, onUnauthorized)
      if (res) return null

      console.error('Failed to find a CRM object:', response.statusText)
      throw new CustomError('Server error status ' + response.status, response.status)
    }

    return (await response.json()) as CrmObject[]
  }
}

async function handleUnAuthorized(response: Response, onUnauthorized?: (text: string) => void) {
  if (!onUnauthorized) return false

  const data = (await response.json()) as { errorCode: string }
  if (response.status === 401) {
    if (data.errorCode === AUTH_ERROR_MESSAGE) {
      onUnauthorized(`useTranslation.text('establishCrmConnection')`)
    } else {
      onUnauthorized(`useTranslation.text('reEstablishCrmConnection')`)
    }

    return true
  }

  return false
}
